// src/services/tokenStorage.js
//
// Single place that knows how the auth tokens are persisted. Everything else
// (request headers, the refresh call, sign-in, logout) goes through here so the
// storage keys are not spread across the app.
//
// Access token  — short lived (1h), sent as `Authorization: Bearer` on API calls.
// Refresh token — long lived (30d), sent only to POST /api/auth/refresh.

const ACCESS_TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const ACCESS_EXPIRES_AT_KEY = "auth_token_expires_at";

function readItem(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    // private mode / storage disabled
    return null;
  }
}

function writeItem(key, value) {
  try {
    if (value === null || value === undefined || value === "") {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, String(value));
    }
  } catch (e) {
    // nothing useful to do — the session just won't persist
  }
}

export const getAccessToken = () => readItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => readItem(REFRESH_TOKEN_KEY);

/* Decode a JWT payload without verifying it. Only used to read `exp` for
   client-side scheduling — the server is still the authority on validity. */
function decodeJwtPayload(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

/**
 * When the current access token stops being accepted, in ms since the epoch.
 * Prefers the value stored at login/refresh, and falls back to the token's own
 * `exp` claim so sessions created before this code shipped still work.
 * Returns 0 when it cannot be determined.
 */
export function getAccessTokenExpiresAt() {
  const stored = Number(readItem(ACCESS_EXPIRES_AT_KEY));
  if (stored > 0) return stored;

  const token = getAccessToken();
  if (!token) return 0;

  const payload = decodeJwtPayload(token);
  return payload && payload.exp ? payload.exp * 1000 : 0;
}

/**
 * Total lifetime of the current access token in ms, from its own `exp` - `iat`
 * claims. 0 when it cannot be determined.
 */
export function getAccessTokenLifetimeMs() {
  const token = getAccessToken();
  if (!token) return 0;

  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp || !payload.iat) return 0;

  return Math.max(0, (payload.exp - payload.iat) * 1000);
}

/**
 * How far ahead of expiry to renew. Capped at a quarter of the token's own
 * lifetime, so a short TTL (e.g. ACCESS_TOKEN_TTL_SECONDS=120 while testing)
 * doesn't leave the token permanently "expiring" and refreshing on every tick.
 */
export function getRefreshSkewMs(maxSkewMs) {
  const lifetime = getAccessTokenLifetimeMs();
  if (!lifetime) return maxSkewMs;
  return Math.min(maxSkewMs, lifetime / 4);
}

/**
 * True when the access token is gone, or expires within the effective skew.
 * `maxSkewMs` is an upper bound — see getRefreshSkewMs.
 */
export function isAccessTokenExpiring(maxSkewMs = 0) {
  if (!getAccessToken()) return true;

  const expiresAt = getAccessTokenExpiresAt();
  // Unknown expiry → don't refresh pre-emptively; the 401 retry path covers it.
  if (!expiresAt) return false;

  return Date.now() + getRefreshSkewMs(maxSkewMs) >= expiresAt;
}

/* Listeners notified whenever the stored tokens change. This is what lets the
   session watcher start on login without SessionProvider being remounted —
   signing in is a client-side navigate(), not a page load. */
const listeners = new Set();

export function subscribeToTokenChanges(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyTokenChange() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (e) {
      // one bad listener must not stop the others
    }
  });
}

/**
 * Persist a token pair from a login or refresh response `data` object.
 * `refresh_token` is optional so a response that omits it (older backend)
 * leaves the existing one untouched instead of wiping the session.
 */
export function saveTokens(data) {
  if (!data) return;

  if (data.token) {
    writeItem(ACCESS_TOKEN_KEY, data.token);

    const ttlSeconds = Number(data.expiresIn);
    writeItem(ACCESS_EXPIRES_AT_KEY, ttlSeconds > 0 ? Date.now() + ttlSeconds * 1000 : null);
  }

  const refreshToken = data.refresh_token || data.refreshToken;
  if (refreshToken) {
    writeItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  notifyTokenChange();
}

export function clearTokens() {
  writeItem(ACCESS_TOKEN_KEY, null);
  writeItem(REFRESH_TOKEN_KEY, null);
  writeItem(ACCESS_EXPIRES_AT_KEY, null);
  notifyTokenChange();
}

/** Any sign that the user is (or was) logged in on this browser. */
export function hasAuthState() {
  return Boolean(getAccessToken() || getRefreshToken() || readItem("user_id"));
}
